import * as React from "react";
import {VeboxModels} from "../../types/veboxModels";
import {VeBoxCard} from "../../components/card/VeBoxCard";
import {UserInfoField} from "../components/UserInfoField";
import {Button, Checkbox, Icon, icons, InputAddon, SelectInputOption, Tooltip} from "@vebox/etui-core";
import {RestDataLoader} from "../../dashboard/widgets/unilever/RestDataLoader";
import {useUserDetails} from "../contexts/UserDetailsContext";
import {Loader} from "../../components/Utils/loader/Loader";
import {Display} from "../../components/Display";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {useCommonData} from "../contexts/CommonDataProvider";
import {DATA_STATE} from "../contexts/UsersDashboardContext";
import {VeboxUser} from "../../providers/VeboxUser";
import {EditableSelectInput} from "../components/EditableSelectInput";
import {BUTTON_STYLE} from "../../components/common/etui_components_styles";
import {ETUI_ICONS_SIZE} from "../UserManagement";
import {MANAGE_HIDDEN_USERS, PersonalData, SettingsData, userHasPermission} from "../../components/Utils/RolesUtils";
import {ErrorType} from "../UserDetails";

declare var AppTranslations: AppTranslations;

interface PatchOperation {
    op: "replace",
    path: valuesPaths,
    value: string | boolean,
}

type UserInfoProps = {
    setError: (errorType: ErrorType) => void
    isCurrentUser: boolean
    editablePersonalData: PersonalData,
    editableSettingsData: SettingsData,
}

type statusColor = {
    statusCode: string, color: string
}

const statusColors: statusColor[] = [
    {statusCode: "true", color: "#14C510"},
    {statusCode: "false", color: "#E2000F"}
]

export enum valuesPaths {
    email = "/email",
    surname = "/surname",
    name = "/name",
    locale = "/locale",
    timezone = "/timezone",
    status = "/status",
    isVisible = "/isVisible",
    receiveEmailNotifications = "/receiveEmailNotifications",
    receivePushNotifications = "/receivePushNotifications"
}

export enum TextInputValues {
    email = "email",
    surname = "surname",
    name = "name",
}

export enum SelectInputValues {
    locale = "locale",
    timezone = "timezone",
    status = "status"
}

export enum CheckBoxValues {
    isVisible = "isVisible",
    receiveEmailNotifications = "receiveEmailNotifications",
    receivePushNotifications = "receivePushNotifications"
}


export type ValuesToUpdate = {
    name: string,
    surname: string,
    email: string,
    isVisible: boolean,
    receiveEmailNotifications: boolean,
    receivePushNotifications: boolean,
    locale: SelectInputOption,
    timezone: SelectInputOption,
    status: SelectInputOption,
}

export enum ValuesToUpdateEnum {
    name = "name",
    surname = "surname",
    email = "email",
    isVisible = "isVisible",
    receiveEmailNotifications = "receiveEmailNotifications",
    receivePushNotifications = "receivePushNotifications",
    locale = "locale",
    timezone = "timezone",
    status = "status",
}

const ROLE_ADMIN = "ROLE_ADMIN";

const getStatusColor = (status: SelectInputOption): string => {
    return statusColors.find((stC: statusColor) => {
        return stC.statusCode === status.value;
    }).color;
}

const areValueEquals = (defaultValues: ValuesToUpdate, currentValues: ValuesToUpdate): boolean => {
    let equals = true

    Object.keys(currentValues).forEach(key => {
        if (typeof currentValues[key] === "object") {
            if (currentValues[key]?.value !== defaultValues[key]?.value) {
                equals = false;
            }
        } else {
            if (currentValues[key] !== defaultValues[key]) {
                equals = false;
            }
        }
    })

    return equals;
}

const createPatchOperationList = (valuesToUpdate: ValuesToUpdate): PatchOperation[] => {
    return Object.keys(valuesToUpdate).map((key) => {
        return {
            op: "replace",
            path: valuesPaths[key],
            value: typeof valuesToUpdate[key] === "object" ? valuesToUpdate[key].value : valuesToUpdate[key]
        }
    })
}

const hasAReadOnlyField = (editablePersonalData: PersonalData,
                           editableSettingsData: SettingsData,
                           isRoleAdmin: boolean): boolean => {

    return !editablePersonalData.allTrueExcludeUserName
        || (isRoleAdmin ? !editableSettingsData.allTrue : !editableSettingsData.allTrueExcludeVisibleEditing)
}

export const UserInfo: React.FC<UserInfoProps> = ({
                                                      setError,
                                                      isCurrentUser,
                                                      editableSettingsData,
                                                      editablePersonalData
                                                  }) => {
    const statusOption: SelectInputOption[] = [{
        value: "true",
        label: AppTranslations?.usersManagement?.activeUser
    }, {
        value: "false",
        label: AppTranslations?.usersManagement?.blockedUser
    }]

    const getLocale = (value: string): SelectInputOption | undefined => {
        return locales.find((loc: SelectInputOption) => loc.value === value);
    }
    const getStatus = (status: boolean): SelectInputOption | undefined => {
        return statusOption.find((stat: SelectInputOption) => stat.value === status.toString());
    }

    const {
        availablePushNotificationEditing,
        availableVisibleEditing,
        availableEnableEditing,
        availableEmailNotificationEditing
    } = editableSettingsData
    const {
        availableSurnameEditing,
        availableLocaleEditing,
        availableNameEditing,
        availableEMailEditing,
        availableTimeZoneEditing
    } = editablePersonalData

    const [currentUser, setCurrentUser] = useUserDetails();
    const loggedInUser = VeboxUser.useCurrentUser();
    const [state, setState] = React.useState<DATA_STATE>(DATA_STATE.isLoading)
    const [localesLoaded, setLocalesLoaded] = React.useState<boolean>(false);
    const [currentUserLoaded, setCurrentUserLoaded] = React.useState<boolean>(false);
    const [isRoleAdmin, setIsRoleAdmin] = React.useState<boolean>(false);
    const [valuesChanged, setValuesChanged] = React.useState<boolean>(false);
    const [isSubmittable, setIsSubmittable] = React.useState<boolean>(false);

    const [valuesToUpdate, setValuesToUpdate] = React.useState<ValuesToUpdate>();
    const [defaultValues, setDefaultValues] = React.useState<ValuesToUpdate>();

    const {locales, timezones} = useCommonData(data => {
        return {
            ...data,
            timezones: data.timezones.map<SelectInputOption>((timezone) => ({
                value: timezone,
                label: timezone,
            })),
            locales: data.locales.map<SelectInputOption>((locale) => ({
                value: locale.name,
                label: locale.displayName,
            }))
        }
    })

    const canManageHiddenUsers: boolean = React.useMemo(() => userHasPermission(loggedInUser, MANAGE_HIDDEN_USERS),
        [loggedInUser?.guid]);

    const updateValue = (name: string, value: any, setADefaultValue = false) => {
        if (setADefaultValue) {
            setDefaultValues(prevValues => ({
                ...prevValues,
                [name]: value
            }))
        } else {
            setValuesToUpdate(prevValues => ({
                ...prevValues,
                [name]: value
            }))
        }

    }

    const setUserInfo = () => {
        updateValue(ValuesToUpdateEnum.locale, getLocale(currentUser.locale), true);
        updateValue(ValuesToUpdateEnum.timezone, {value: currentUser.timezone, label: currentUser.timezone}, true);
        updateValue(ValuesToUpdateEnum.status, getStatus(currentUser.enabled), true);
        updateValue(ValuesToUpdateEnum.isVisible, currentUser.visible, true);
        updateValue(ValuesToUpdateEnum.receiveEmailNotifications, currentUser.receiveEmailNotifications, true);
        updateValue(ValuesToUpdateEnum.receivePushNotifications, currentUser.receivePushNotifications, true);
        updateValue(ValuesToUpdateEnum.name, currentUser.name, true);
        updateValue(ValuesToUpdateEnum.surname, currentUser.surname, true);
        updateValue(ValuesToUpdateEnum.email, currentUser.email, true);
        setIsRoleAdmin(currentUser.rolesAndPermissions.find((role) => role.code === ROLE_ADMIN) !== undefined);
        setCurrentUserLoaded(true);
    }

    const setErrorState = () => {
        setError(ErrorType.UPDATING_ERROR)
        setState(DATA_STATE.error);
    }

    const patchUser = (): void => {
        setCurrentUserLoaded(false);
        updateUser(currentUser.guid, createPatchOperationList(valuesToUpdate))
            .then(setCurrentUser)
            .catch(setErrorState)
    }

    const handleOnTextInputChange = (name: TextInputValues) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            updateValue(name, event.target.value);
        }
    }
    const handleOnSelectInputChange = (name: SelectInputValues) => {
        return (option: SelectInputOption) => {
            updateValue(name, option);
        }
    }

    const handleOnCheckBoxChange = (name: CheckBoxValues) => {
        return (event) => {
            const newValue: boolean = event.target.checked;
            updateValue(name, name === CheckBoxValues.isVisible ? !newValue : newValue);
        };
    }

    const getErrorMessage = (errorMessage: string) => {
        setIsSubmittable(errorMessage === "");
    }

    const resetSingleValue = (name: TextInputValues | SelectInputValues) => () => {
        updateValue(name, defaultValues[name])
    }
    const resetValues = () => {
        setValuesToUpdate(defaultValues);
    }

    React.useEffect(() => {
        if (locales.length > 0 && timezones.length > 0) {
            setLocalesLoaded(true);
        }
    }, [locales, timezones]);

    React.useEffect(() => {
        if (currentUser && localesLoaded) {
            setUserInfo();
        }
    }, [currentUser, localesLoaded]);

    React.useEffect(() => {
        setState(!currentUserLoaded || !localesLoaded ? DATA_STATE.isLoading : DATA_STATE.loaded);
    }, [currentUserLoaded, localesLoaded]);


    React.useEffect(() => {
        if (currentUserLoaded) {
            setValuesChanged(valuesToUpdate ? !areValueEquals(defaultValues, valuesToUpdate) : false);
        }
    }, [valuesToUpdate, currentUserLoaded])

    const showBack = isCurrentUser || hasAReadOnlyField(editablePersonalData, editableSettingsData, isRoleAdmin);

    return (
        <VeBoxCard className={"Section"}>
            <VeBoxCard.Header
                title={AppTranslations.userCreationProcess.infoStageTitle.toUpperCase()}
                singleLineTitle={true}
                className={!showBack ? "padding-bottom-13 padding-top-13" : ""}
                back={showBack
                    ? <Tooltip
                        message={isCurrentUser
                            ? AppTranslations.usersManagement.fieldsDisabledForHimselfMessage
                            : AppTranslations.usersManagement.fieldsDisabledForReaOnlyUserMessage}
                        placement={"left"}
                    >
                        <Icon glyph={icons.INFO_CIRCLE} size={ETUI_ICONS_SIZE}/>
                    </Tooltip>
                    : <></>}
            />
            <VeBoxCard.Body className={"detail-card"}>
                <Display isVisible={state === DATA_STATE.isLoading}>
                    <Loader/>
                </Display>
                <Display isVisible={state === DATA_STATE.loaded && Boolean(currentUser)}>
                    <div className={"users-management-info-container"}>
                        <UserInfoField
                            value={valuesToUpdate?.name ?? defaultValues?.name}
                            defaultValue={defaultValues?.name}
                            label={AppTranslations.userCreationProcess.name}
                            path={TextInputValues.name}
                            readOnly={!availableNameEditing}
                            maxValueLength={200}
                            onChange={handleOnTextInputChange(TextInputValues.name)}
                            getErrorMessage={getErrorMessage}
                            onCancel={resetSingleValue(TextInputValues.name)}
                        />
                        <UserInfoField
                            value={valuesToUpdate?.surname ?? defaultValues?.surname}
                            defaultValue={defaultValues?.surname}
                            label={AppTranslations.userCreationProcess.surname}
                            path={TextInputValues.surname}
                            readOnly={!availableSurnameEditing}
                            maxValueLength={200}
                            onChange={handleOnTextInputChange(TextInputValues.surname)}
                            getErrorMessage={getErrorMessage}
                            onCancel={resetSingleValue(TextInputValues.surname)}
                        />
                        <UserInfoField
                            defaultValue={currentUser?.username}
                            label={AppTranslations.userCreationProcess.username}
                            readOnly={true}
                        />
                        <UserInfoField
                            defaultValue={currentUser?.company?.name}
                            label={AppTranslations.genericWords.company}
                            readOnly={true}
                        />
                        <UserInfoField
                            value={valuesToUpdate?.email ?? defaultValues?.email}
                            defaultValue={defaultValues?.email}
                            label={AppTranslations.userCreationProcess.email}
                            path={TextInputValues.email}
                            readOnly={!availableEMailEditing}
                            maxValueLength={200}
                            onChange={handleOnTextInputChange(TextInputValues.email)}
                            getErrorMessage={getErrorMessage}
                            onCancel={resetSingleValue(TextInputValues.email)}
                        />
                        <div className={"user-detail-select-input"}>
                            <EditableSelectInput
                                name={"language"}
                                label={AppTranslations.userCreationProcess.locale}
                                options={locales}
                                value={valuesToUpdate?.locale ?? defaultValues?.locale}
                                defaultValue={defaultValues?.locale}
                                searchable={true}
                                onChange={handleOnSelectInputChange(SelectInputValues.locale)}
                                readOnly={!availableLocaleEditing}
                                onCancel={resetSingleValue(SelectInputValues.locale)}
                            />
                        </div>
                        <div className={"user-detail-select-input"}>
                            <EditableSelectInput
                                name={"timezone"}
                                label={AppTranslations.userCreationProcess.timezone}
                                options={timezones}
                                value={valuesToUpdate?.timezone ?? defaultValues?.timezone}
                                defaultValue={defaultValues?.timezone}
                                searchable={true}
                                onChange={handleOnSelectInputChange(SelectInputValues.timezone)}
                                readOnly={!availableTimeZoneEditing}
                                onCancel={resetSingleValue(SelectInputValues.timezone)}
                            />
                        </div>
                        <div className={"user-detail-select-input"}>
                            <EditableSelectInput
                                name="status"
                                label={AppTranslations.usersManagement.userStatus}
                                options={statusOption}
                                value={valuesToUpdate?.status ?? defaultValues?.status}
                                defaultValue={defaultValues?.status}
                                readOnly={!availableEnableEditing}
                                startInputAddon={
                                    <InputAddon position="start">
                                            <span
                                                className="material-icons"
                                                style={{
                                                    color: (valuesToUpdate?.status ?? defaultValues?.status)
                                                        ? getStatusColor(
                                                            valuesToUpdate?.status ?? defaultValues?.status)
                                                        : "#14C510"
                                                }}
                                            >
                                                circle
                                            </span>
                                    </InputAddon>
                                }
                                onChange={handleOnSelectInputChange(SelectInputValues.status)}
                                onCancel={resetSingleValue(SelectInputValues.status)}
                            />
                        </div>
                        <div className={"margin-left-10"}>
                            <Display isVisible={canManageHiddenUsers}>
                                <div className={"row margin-top-10"}>
                                    <span>
                                        <Checkbox
                                            name={"hide_user"}
                                            checked={(valuesToUpdate?.isVisible !== undefined) ? !valuesToUpdate?.isVisible : !defaultValues?.isVisible}
                                            disabled={isRoleAdmin || !availableVisibleEditing}
                                            onChange={handleOnCheckBoxChange(CheckBoxValues.isVisible)}
                                        />
                                        <span className={"padding-left-10 font-weight-400"}>{AppTranslations.userCreationProcess.invisibleUser}</span>
                                    </span>
                                </div>
                            </Display>
                            <div className={"row margin-top-10"}>
                                <span>
                                        <Checkbox
                                            name={"send_notification"}
                                            checked={valuesToUpdate?.receivePushNotifications ?? defaultValues?.receivePushNotifications}
                                            disabled={!availablePushNotificationEditing}
                                            onChange={handleOnCheckBoxChange(CheckBoxValues.receivePushNotifications)}
                                        />
                                    <span className={"padding-left-10 font-weight-400"}>{AppTranslations.userCreationProcess.pushNotifications}</span>
                                </span>
                            </div>
                            <div className={"row margin-top-10"}>
                                <span>
                                        <Checkbox
                                            name={"subscribe_newsletter"}
                                            checked={valuesToUpdate?.receiveEmailNotifications ?? defaultValues?.receiveEmailNotifications}
                                            disabled={!availableEmailNotificationEditing}
                                            onChange={handleOnCheckBoxChange(CheckBoxValues.receiveEmailNotifications)}
                                        />
                                    <span className={"padding-left-10 font-weight-400"}>{AppTranslations.userCreationProcess.subscribeToNewsletter}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <Display isVisible={valuesChanged}>
                        <div className={"user-details-buttons-section"}>
                            <Button
                                onClick={resetValues}
                                variant="tertiary"
                                style={{minWidth: 70}}
                                styles={BUTTON_STYLE}
                            >
                                {AppTranslations.genericWords.cancel}
                            </Button>
                            <Tooltip
                                message={AppTranslations.usersManagement.errorUserWithoutRoles}
                                disableToolTip={isSubmittable}
                                placement={"top"}
                            >
                                <Button
                                    onClick={patchUser}
                                    variant="strong"
                                    style={{minWidth: 70}}
                                    styles={BUTTON_STYLE}
                                    disabled={!isSubmittable}
                                >
                                    {AppTranslations.genericWords.submit}
                                </Button>
                            </Tooltip>
                        </div>
                    </Display>
                </Display>
            </VeBoxCard.Body>
        </VeBoxCard>
    )
}

function updateUser(userGuid: string, operationList: PatchOperation[]): Promise<VeboxModels.User> {
    return new Promise<VeboxModels.User>((res, rej) => {
        RestDataLoader.patchJSON(
            "users/" + userGuid,
            {
                list: operationList,
            },
            res,
            rej
        );
    });
}